import React, { useState, useEffect, useCallback, useMemo } from "react";
import { NavLink, useNavigate, useLocation, useParams } from "react-router-dom";
import { useAuth, authFetch } from "../App";
import { useUIStore } from "../store/uiStore";
import { useTranslation } from "../i18n/LanguageProvider";
import { useGreeting } from "../hooks/useGreeting";
import SidebarEditor from "./SidebarEditor";
import type { SidebarLayoutConfig, SidebarSectionConfig } from "./SidebarEditor";
import { toast } from "sonner";
import { BUILD_LABEL, BUILD_VERSION, BUILD_DATE, BUILD_COMMIT } from "../buildInfo";
import ChangelogModal from "../components/ChangelogModal";
import {
  FolderOpen,
  Mail,
  Users,
  UsersRound,
  CalendarDays,
  CalendarCheck,
  Table2,
  Timer,
  Clock,
  Receipt,
  LogOut,
  LayoutGrid,
  Pin,
  PinOff,
  Bell,
  ChevronDown,
  MapPin,
  X,
  CheckCheck,
  Activity,
  Calculator,
  Shield,
  ScrollText,
  ListTodo,
  ClipboardList,
  FileText,
  TrendingUp,
  ArrowLeft,
  KeyRound,
  Lightbulb,
  Sun,
  Moon,
  Settings,
  Map,
  Pencil,
  Inbox,
  Brain,
  Zap,
  UserPlus,
} from "lucide-react";

/* ── Section-based nav structure ── */

const NAV_SECTIONS = [
  {
    label: "OPERATIV",
    items: [
      { to: "/app", icon: FolderOpen, labelKey: "nav.projekte", end: true, testId: "nav-projekte", visId: "projekte" },
      { to: "/app/projektzeitenplanung", icon: CalendarDays, labelKey: "nav.projektzeitenplanung", labelFallback: "Projektzeitenplanung", testId: "nav-projektzeitenplanung", visId: "projektzeitenplanung" },
      { to: "/app/einsatzplanung", icon: CalendarDays, labelKey: "nav.einsatzplanung", testId: "nav-einsatzplanung", visId: "einsatzplanung" },
      { to: "/app/tagesuebersicht", icon: Clock, labelKey: "nav.tagesuebersicht", testId: "nav-tagesuebersicht", visId: "tagesuebersicht" },
      { to: "/app/stempeluhr", icon: Timer, labelKey: "nav.stempeluhr", testId: "nav-stempeluhr", visId: "stempeluhr" },
    ],
  },
  {
    label: "CONTROLLING",
    items: [
      { to: "/app/dashboard-admin", icon: Shield, labelKey: "nav.admindashboard", adminOnly: true, testId: "nav-admindashboard", visId: "admin_dashboard" },
      { to: "/app/stundenkonten", icon: Table2, labelKey: "nav.stundenkonten", testId: "nav-stundenkonten", visId: "stundenkonten" },
      { to: "/app/wochenansicht", icon: CalendarDays, labelKey: "nav.wochenansicht", adminOnly: true, testId: "nav-wochenansicht", visId: "wochenansicht" },
      { to: "/app/monatsansicht", icon: CalendarDays, labelKey: "nav.monatsansicht", adminOnly: true, testId: "nav-monatsansicht", visId: "monatsansicht" },
    ],
  },
  {
    label: "ORGANISATION",
    items: [
      { to: "/app/mitarbeiter", icon: Users, labelKey: "nav.mitarbeiter", adminOnly: true, testId: "nav-mitarbeiter", visId: "mitarbeiter" },
      { to: "/app/teams", icon: UsersRound, labelKey: "nav.teams", adminOnly: true, testId: "nav-teams", visId: "teams" },
      { to: "/app/terminplaner", icon: CalendarCheck, labelKey: "nav.terminplaner", testId: "nav-terminplaner", roles: ["admin", "projektleiter"], visId: "terminplaner" },
    ],
  },
  {
    label: "BUSINESS",
    items: [
      { to: "/app/crm", icon: LayoutGrid, labelKey: "nav.crm", permission: "can_access_crm", testId: "nav-crm", visId: "crm" },
      { to: "/app/belegpilot", icon: Receipt, labelKey: "nav.belegpilot", label: "Beleg Pilot", testId: "nav-belegpilot", visId: "belegpilot", nestedPermission: "belegpilot.access", rolesAllow: ["admin", "geschaeftsfuehrung", "geschäftsführung", "projektleiter", "buero", "büro"], adminPermissionAllow: "can_manage_users" },
      { to: "/app/abrechnungen", icon: Receipt, labelKey: "nav.abrechnungen", adminOnly: true, testId: "nav-abrechnungen", visId: "abrechnungen" },
      { to: "/app/passwort-tresor", icon: KeyRound, labelKey: "nav.passworttresor", roles: ["admin"], testId: "nav-passworttresor", visId: "passwort_tresor" },
    ],
  },
  {
    label: "SYSTEM",
    items: [
      { to: "/app/nina", icon: Brain, labelKey: "nav.nina", label: "Nina", testId: "nav-nina", visId: "nina_studio", ninaGuarded: true },
      { to: "/app/nina/gaeste", icon: UserPlus, label: "Nina-Gäste", testId: "nav-nina-gaeste", visId: "nina_gaeste", nestedPermission: "nina_guest_invite_create", rolesAllow: ["admin"] },
      { to: "/app/system/integrationen/nina-ai", icon: Zap, labelKey: "nav.integration_nina", label: "Integration: Nina.ai", adminOnly: true, testId: "nav-integration-nina", visId: "integration_nina" },
      { to: "/app/app-einstellungen", icon: Settings, labelKey: "nav.appeinstellungen", adminOnly: true, testId: "nav-appeinstellungen", visId: "app_einstellungen" },
      { to: "/app/system-historie", icon: ScrollText, labelKey: "nav.systemhistorie", adminOnly: true, alwaysShowAdmin: true, testId: "nav-systemhistorie", visId: "system_historie" },
    ],
  },
];

/* ── Flat map of all items by visId (for editor + config merge) ── */
const ALL_NAV_ITEMS: Record<string, any> = {};
const ITEM_LABELS: Record<string, string> = {};
NAV_SECTIONS.forEach(s => s.items.forEach(item => {
  ALL_NAV_ITEMS[item.visId] = item;
  ITEM_LABELS[item.visId] = (item as any).label || item.labelKey;
}));

/* ── Build config from NAV_SECTIONS (default) ── */
function getDefaultConfig(): SidebarLayoutConfig {
  return {
    sections: NAV_SECTIONS.map((s, i) => ({
      id: s.label.toLowerCase().replace(/\s+/g, "_"),
      label: s.label,
      items: s.items.map(item => ({ visId: item.visId })),
    })),
  };
}

/* ── Merge saved config with current NAV_SECTIONS (handles new/removed items) ──
 * WICHTIG (Fix iter_347): Neue visIds werden NICHT mehr blind an die letzte
 * Section angehängt. Stattdessen versuchen wir, sie an ihrer Default-Position
 * (aus NAV_SECTIONS) in die gespeicherte Config einzusortieren:
 *   1. Ermittle die Default-Section (Label) für die fehlende visId
 *   2. Ermittle den direkten Default-Vorgänger in dieser Section
 *   3. Wenn diese Section (per label ODER id) in der saved-Config existiert
 *      → füge das Item direkt hinter dem Vorgänger ein (falls vorhanden),
 *        sonst am Anfang der Section
 *   4. Wenn die Section nicht existiert, lege sie in der Default-Reihenfolge an
 */
function _defaultSectionLabelFor(visId: string): string | null {
  for (const s of NAV_SECTIONS) {
    if (s.items.some(i => i.visId === visId)) return s.label;
  }
  return null;
}
function _defaultPrevVisIdInSection(visId: string): string | null {
  for (const s of NAV_SECTIONS) {
    const idx = s.items.findIndex(i => i.visId === visId);
    if (idx > 0) return s.items[idx - 1].visId;
    if (idx === 0) return null;
  }
  return null;
}
function _sectionMatches(section: SidebarSectionConfig, defaultLabel: string): boolean {
  const wantId = defaultLabel.toLowerCase().replace(/\s+/g, "_");
  return section.label === defaultLabel || section.id === wantId;
}
function _insertMissingIntoSaved(saved: SidebarLayoutConfig, visId: string) {
  const prevVisId = _defaultPrevVisIdInSection(visId);

  // 1) Global-Suche: Wenn der Default-Vorgänger in irgendeiner Section liegt,
  //    fügen wir das Item direkt dahinter in der gleichen Section ein
  //    (z.B. `projektzeitenplanung` landet direkt hinter `projekte`,
  //    egal ob `projekte` in OPERATIV oder BUSINESS steht).
  if (prevVisId) {
    for (const section of saved.sections) {
      const idx = section.items.findIndex(it => it.visId === prevVisId);
      if (idx >= 0) {
        section.items.splice(idx + 1, 0, { visId });
        return;
      }
    }
  }

  // 2) Fallback: Default-Section verwenden (ggf. anlegen)
  const defaultLabel = _defaultSectionLabelFor(visId);
  if (!defaultLabel) return;
  let targetSection = saved.sections.find(s => _sectionMatches(s, defaultLabel));
  if (!targetSection) {
    const defaultOrder = NAV_SECTIONS.map(s => s.label);
    const targetOrderIdx = defaultOrder.indexOf(defaultLabel);
    let insertAt = saved.sections.length;
    for (let i = 0; i < saved.sections.length; i++) {
      const savedIdx = defaultOrder.indexOf(saved.sections[i].label);
      if (savedIdx >= 0 && savedIdx > targetOrderIdx) { insertAt = i; break; }
    }
    targetSection = {
      id: defaultLabel.toLowerCase().replace(/\s+/g, "_"),
      label: defaultLabel,
      items: [],
    };
    saved.sections.splice(insertAt, 0, targetSection);
  }
  targetSection.items.unshift({ visId });
}
function mergeConfig(saved: SidebarLayoutConfig): SidebarLayoutConfig {
  // Collect all visIds from saved config (sections + subsections)
  const usedIds = new Set<string>();
  saved.sections.forEach(s => {
    s.items.forEach(item => usedIds.add(item.visId));
    (s.subsections || []).forEach(ss => {
      ss.items.forEach(item => usedIds.add(item.visId));
    });
  });

  // Find items not in saved config (newly added to code)
  const allIds = Object.keys(ALL_NAV_ITEMS);
  const missingIds = allIds.filter(id => !usedIds.has(id));

  // Missing items an ihrer Default-Position in die saved-Config einsortieren
  missingIds.forEach(visId => _insertMissingIntoSaved(saved, visId));

  // Remove items that no longer exist in code (from both sections and subsections)
  saved.sections = saved.sections.map(s => ({
    ...s,
    items: s.items.filter(item => ALL_NAV_ITEMS[item.visId]),
    subsections: (s.subsections || []).map(ss => ({
      ...ss,
      items: ss.items.filter(item => ALL_NAV_ITEMS[item.visId]),
    })),
  }));

  return saved;
}

/* ── Project context nav items ── */
const getProjectNavItems = (projectId) => [
  { to: `/app/projekte/${projectId}/aufgaben`, icon: ListTodo, labelKey: "ctx.aufgaben", testId: "ctx-aufgaben" },
  { to: `/app/projekte/${projectId}/berichte`, icon: FileText, labelKey: "ctx.berichte", testId: "ctx-berichte" },
  { to: `/app/projekte/${projectId}/plaene`, icon: Map, labelKey: "ctx.plaene", testId: "ctx-plaene" },
  { to: `/app/projekte/${projectId}/controlling`, icon: TrendingUp, labelKey: "ctx.controlling", testId: "ctx-controlling" },
  { to: "/app/einsatzplanung", icon: CalendarDays, labelKey: "ctx.einsatzplanung", testId: "ctx-einsatzplanung" },
  { to: `/app/projekte/${projectId}/dashboard`, icon: FolderOpen, labelKey: "ctx.uebersicht", testId: "ctx-uebersicht" },
];

const COLLAPSED_W = 56;
const EXPANDED_W = 220;
const MIN_W = 200;
const MAX_W = 400;
const EDIT_MIN_W = 300;

function ThemeToggle(_: { isExpanded: boolean }) {
  // Light Mode wurde entfernt — TGAone läuft dauerhaft im dunklen Glas-Mode.
  // Diese Komponente bleibt als No-Op erhalten, falls noch ein interner Aufruf existiert.
  return null;
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hovered, setHovered] = useState(false);
  const pinned = useUIStore((s) => s.sidebarPinned);
  const togglePin = useUIStore((s) => s.toggleSidebarPin);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notifPanelRef = React.useRef(null);
  const notifBtnRef = React.useRef(null);

  /* ── Sidebar Config ── */
  const [sidebarConfig, setSidebarConfig] = useState<SidebarLayoutConfig | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [userWidth, setUserWidth] = useState<number>(() => {
    const saved = Number(localStorage.getItem("sidebar-width"));
    return saved >= MIN_W && saved <= MAX_W ? saved : EXPANDED_W;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [collapsedSubs, setCollapsedSubs] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem("sidebar-subs-collapsed");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });

  const toggleSubCollapse = (key: string) => {
    setCollapsedSubs(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      localStorage.setItem("sidebar-subs-collapsed", JSON.stringify(Array.from(next)));
      return next;
    });
  };

  const expandedWidth = editMode ? Math.max(userWidth, EDIT_MIN_W) : userWidth;

  // Persist width
  useEffect(() => {
    localStorage.setItem("sidebar-width", String(userWidth));
  }, [userWidth]);

  // Resize handler
  useEffect(() => {
    if (!isResizing) return;
    const handleMove = (e: MouseEvent) => {
      const w = Math.min(MAX_W, Math.max(MIN_W, e.clientX));
      setUserWidth(w);
    };
    const handleUp = () => setIsResizing(false);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [isResizing]);

  const isAdmin = user?.role === "admin";
  const isExpanded = pinned || hovered;
  const { t } = useTranslation();
  const { text: greetingText } = useGreeting(user);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [ninaMenuEnabled, setNinaMenuEnabled] = useState<boolean>(true);
  const [ninaCanAccess, setNinaCanAccess] = useState<boolean>(false);

  // Nina Menu-Setting + Access laden (Live-Check via API, damit
  // Server-Migrationen sofort greifen ohne Re-Login)
  useEffect(() => {
    let alive = true;
    authFetch("/api/nina-engine/settings/menu-visibility")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive) return;
        if (d && typeof d.enabled_for_admins === "boolean") setNinaMenuEnabled(d.enabled_for_admins);
      })
      .catch(() => {});
    authFetch("/api/nina-engine/access")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive) return;
        setNinaCanAccess(!!d?.access);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  /* ── Load sidebar config ── */
  useEffect(() => {
    authFetch("/api/sidebar-config").then(r => r.ok ? r.json() : null).then(d => {
      if (d?.config) {
        setSidebarConfig(mergeConfig(d.config));
      }
    }).catch(() => {});
  }, []);

  const handleSaveConfig = async (config: SidebarLayoutConfig) => {
    try {
      const res = await authFetch("/api/sidebar-config", {
        method: "PUT",
        body: JSON.stringify({ config }),
      });
      if (res.ok) {
        setSidebarConfig(config);
        setEditMode(false);
        toast.success("Sidebar gespeichert!");
      } else {
        toast.error("Fehler beim Speichern");
      }
    } catch {
      toast.error("Netzwerkfehler");
    }
  };

  const handleResetConfig = async () => {
    if (!window.confirm("Sidebar auf Standard zuruecksetzen?")) return;
    try {
      await authFetch("/api/sidebar-config", { method: "DELETE" });
      setSidebarConfig(null);
      setEditMode(false);
      toast.success("Sidebar zurueckgesetzt!");
    } catch {}
  };

  /* ── Build editor item map ── */
  const editorItemMap = useMemo(() => {
    const map: Record<string, { label: string; icon: any }> = {};
    NAV_SECTIONS.forEach(s => s.items.forEach(item => {
      map[item.visId] = {
        label: (item as any).label || t(item.labelKey),
        icon: item.icon,
      };
    }));
    return map;
  }, [t]);

  /* ── Detect project context ── */
  const projectMatch = location.pathname.match(/^\/app\/projekte\/([^/]+)/);
  const inProjectContext = !!projectMatch;
  const contextProjectId = projectMatch?.[1] || "";

  // Poll unread count
  const fetchUnread = useCallback(async () => {
    try {
      const res = await authFetch("/api/notifications/unread-count");
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.count || 0);
      }
    } catch {}
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await authFetch("/api/notifications");
      if (res.ok) setNotifications(await res.json());
    } catch {}
  }, []);

  // WebSocket for real-time notifications (with polling fallback)
  const wsRef = React.useRef(null);
  const wsReconnectTimer = React.useRef(null);

  const connectWebSocket = useCallback(() => {
    if (!isAdmin) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsUrl = `${wsProtocol}//${window.location.host}/api/ws/notifications`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        ws.send(JSON.stringify({ token }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "unread_count") {
            setUnreadCount(data.count || 0);
          } else if (data.type === "new_notification") {
            setUnreadCount((c) => c + 1);
            if (notifOpen) fetchNotifications();
          }
        } catch {}
      };

      ws.onclose = () => {
        wsRef.current = null;
        wsReconnectTimer.current = setTimeout(connectWebSocket, 5000);
      };

      ws.onerror = () => {
        ws.close();
      };

      wsRef.current = ws;
    } catch {
      // Fallback to polling if WebSocket fails
    }
  }, [isAdmin, notifOpen, fetchNotifications]);

  useEffect(() => {
    if (!isAdmin) return;
    connectWebSocket();
    const interval = setInterval(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        fetchUnread();
      }
    }, 30000);
    return () => {
      clearInterval(interval);
      if (wsReconnectTimer.current) clearTimeout(wsReconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isAdmin, connectWebSocket, fetchUnread]);

  const handleBellClick = () => {
    if (!notifOpen) fetchNotifications();
    setNotifOpen(!notifOpen);
  };

  // Close panel on outside click
  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e) => {
      if (
        notifPanelRef.current && !notifPanelRef.current.contains(e.target) &&
        notifBtnRef.current && !notifBtnRef.current.contains(e.target)
      ) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [notifOpen]);

  const markRead = async (id) => {
    await authFetch(`/api/notifications/${id}/read`, { method: "PUT" });
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
    setUnreadCount((c) => Math.max(0, c - 1));
  };

  const markAllRead = async () => {
    await authFetch("/api/notifications/read-all", { method: "PUT" });
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const adminMode = useUIStore((s) => s.adminMode);

  /* ── Filter items by role/permission + app_visibility ── */
  const filterItem = useCallback((item) => {
    // Admin with custom visibility? Check visibility map (admin can override own view via settings)
    const vis = user?.app_visibility;
    if (vis && item.visId && vis[item.visId] === false) return false;

    // Nina-Guard: nur Admins mit Zugriff + globalem Menü-Toggle
    if (item.ninaGuarded) {
      if (user?.role !== "admin") return false;
      if (!ninaMenuEnabled) return false;
      // Live-Check via API + Fallback auf User-Permission (falls API noch nicht geladen)
      const hasAccess = ninaCanAccess || !!user?.permissions?.nina_access;
      if (!hasAccess) return false;
      return true;
    }

    if (!adminMode && isAdmin) {
      if (item.adminOnly && !item.alwaysShowAdmin) return false;
      if (item.roles && !item.roles.includes("mitarbeiter")) return false;
    }
    // nestedPermission ("a.b.c") in combination with rolesAllow (always show for these roles)
    if (item.nestedPermission) {
      const role = (user?.role || "").toLowerCase();
      if ((item.rolesAllow || []).includes(role)) return true;
      // Admin-äquivalente Permission-Flag (z. B. can_manage_users)
      if (item.adminPermissionAllow && user?.permissions?.[item.adminPermissionAllow] === true) return true;
      const path = String(item.nestedPermission).split(".");
      let cur = user?.permissions || {};
      for (const seg of path) {
        if (cur && typeof cur === "object") cur = cur[seg];
        else { cur = undefined; break; }
      }
      return !!cur;
    }
    if (item.roles) return item.roles.includes(user?.role);
    if (item.permission) return !!user?.permissions?.[item.permission];
    if (item.adminOnly) return user?.permissions?.can_manage_users || user?.role === "admin";
    return true;
  }, [adminMode, isAdmin, user, ninaMenuEnabled, ninaCanAccess]);

  /* ── Build visible sections (merge with saved config if available) ── */
  const visibleSections = useMemo(() => {
    const mapItems = (items) => items
      .filter(ci => !ci.hidden && ALL_NAV_ITEMS[ci.visId])
      .map(ci => ({
        ...ALL_NAV_ITEMS[ci.visId],
        ...(ci.customLabel ? { label: ci.customLabel, labelKey: undefined } : {}),
      }))
      .filter(filterItem);

    if (sidebarConfig) {
      return sidebarConfig.sections.map(section => ({
        label: section.label,
        items: mapItems(section.items),
        subsections: (section.subsections || []).map(sub => ({
          label: sub.label,
          items: mapItems(sub.items),
        })).filter(sub => sub.items.length > 0),
      })).filter(section => section.items.length > 0 || (section.subsections && section.subsections.length > 0));
    }
    return NAV_SECTIONS.map(section => ({
      ...section,
      items: section.items.filter(filterItem),
      subsections: [],
    })).filter(section => section.items.length > 0);
  }, [filterItem, sidebarConfig]);

  /* ── Project context items ── */
  const projectNavItems = useMemo(() => {
    if (!inProjectContext) return [];
    return getProjectNavItems(contextProjectId);
  }, [inProjectContext, contextProjectId]);

  /* ── Render nav link ── */
  const renderNavLink = (item) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        `flex items-center gap-2.5 mx-1.5 my-0.5 rounded-lg text-[12px] font-medium whitespace-nowrap overflow-hidden transition-all duration-150 ${
          isExpanded ? "px-2.5 py-[8px]" : "justify-center px-1.5 py-2"
        }`
      }
      style={({ isActive }) => ({
        backgroundColor: isActive ? "var(--tga-sb-active-bg)" : undefined,
        color: isActive ? "var(--tga-sb-text-active)" : "var(--tga-sb-text)",
        boxShadow: isActive ? `inset 3px 0 0 0 var(--tga-sb-text-active)` : undefined,
        fontWeight: isActive ? 700 : 500,
      })}
      onMouseEnter={(e) => {
        const isActive = e.currentTarget.getAttribute("aria-current") === "page";
        if (!isActive) {
          e.currentTarget.style.backgroundColor = "var(--tga-sb-hover-bg)";
          e.currentTarget.style.color = "var(--tga-sb-text-hover)";
        }
      }}
      onMouseLeave={(e) => {
        const isActive = e.currentTarget.getAttribute("aria-current") === "page";
        if (!isActive) {
          e.currentTarget.style.backgroundColor = "";
          e.currentTarget.style.color = "var(--tga-sb-text)";
        }
      }}
      title={!isExpanded ? (item.labelKey === "nav.stundenkonten" && !isAdmin ? "Mein Stundenkonto" : (item.label || t(item.labelKey))) : undefined}
      data-testid={item.testId}
    >
      <item.icon className="w-4 h-4 shrink-0" />
      <span
        className="overflow-hidden"
        style={{ opacity: isExpanded ? 1 : 0, width: isExpanded ? "auto" : 0, transition: "opacity 0.15s ease" }}
      >
        {item.labelKey === "nav.stundenkonten" && !isAdmin ? "Mein Stundenkonto" : (item.label || t(item.labelKey))}
      </span>
    </NavLink>
  );

  return (
    <aside
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="fixed left-0 top-0 h-screen flex flex-col z-40"
      style={{
        width: isExpanded ? expandedWidth : COLLAPSED_W,
        backgroundColor: "var(--tga-sb-bg)",
        borderRight: "1px solid var(--tga-sb-border)",
        boxShadow: "var(--tga-sb-shadow)",
        transition: isResizing ? "none" : "width 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      data-testid="app-sidebar"
    >
      {/* Logo + Pin */}
      <div
        className="flex flex-col shrink-0"
        style={{ borderBottom: "1px solid var(--tga-sb-border)" }}
      >
        <div className="flex items-center px-3" style={{ height: 44 }}>
          <div
            className="flex items-center gap-2 cursor-pointer flex-1 overflow-hidden"
            onClick={() => navigate("/app")}
            data-testid="sidebar-logo"
          >
            <img
              src="/logo-dark.png"
              alt="TGAone"
              className="h-6 w-auto flex-shrink-0"
              style={{ filter: "var(--tga-sb-logo-filter)" }}
            />
            {isExpanded && (
              <span
                className="text-[14px] font-bold tracking-tight whitespace-nowrap"
                style={{ color: "var(--tga-sb-logo)", transition: "opacity 0.15s ease" }}
              >
                TGAone
              </span>
            )}
          </div>
          {isExpanded && (
            <div className="flex items-center gap-0.5 flex-shrink-0">
              {isAdmin && (
                <button
                  onClick={() => setEditMode(true)}
                  className="p-1 rounded transition-colors"
                  style={{ color: "var(--tga-sb-icon-dim)" }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = "var(--tga-sb-text)"; e.currentTarget.style.backgroundColor = "var(--tga-sb-hover-bg)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = "var(--tga-sb-icon-dim)"; e.currentTarget.style.backgroundColor = ""; }}
                  title="Sidebar bearbeiten"
                  data-testid="sidebar-edit-btn"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                onClick={togglePin}
                className="p-1 rounded transition-colors"
                style={{
                  color: pinned ? "var(--tga-sb-text-hover)" : "var(--tga-sb-icon-dim)",
                  backgroundColor: pinned ? "var(--tga-sb-pin-bg)" : undefined,
                }}
                onMouseEnter={(e) => { if (!pinned) { e.currentTarget.style.color = "var(--tga-sb-text)"; e.currentTarget.style.backgroundColor = "var(--tga-sb-hover-bg)"; }}}
                onMouseLeave={(e) => { if (!pinned) { e.currentTarget.style.color = "var(--tga-sb-icon-dim)"; e.currentTarget.style.backgroundColor = ""; }}}
                title={pinned ? "Sidebar loesen" : "Sidebar anheften"}
                data-testid="sidebar-pin-btn"
              >
                {pinned ? <Pin className="w-3.5 h-3.5" /> : <PinOff className="w-3.5 h-3.5" />}
              </button>
            </div>
          )}
        </div>
        {isExpanded && greetingText && (
          <div className="px-3 pb-2" data-testid="sidebar-greeting">
            <span
              className="text-[12px] font-medium"
              style={{ color: "var(--tga-sb-text)", opacity: 0.65 }}
            >
              {greetingText}
            </span>
          </div>
        )}
      </div>

      {/* Nav */}
      {editMode && isExpanded ? (
        /* ── EDIT MODE ── */
        <SidebarEditor
          config={sidebarConfig || getDefaultConfig()}
          allItems={editorItemMap}
          onSave={handleSaveConfig}
          onCancel={() => setEditMode(false)}
          onReset={handleResetConfig}
        />
      ) : (
      <nav className="flex-1 py-1 overflow-y-auto overflow-x-hidden" data-testid="sidebar-nav">
        {inProjectContext ? (
          /* ── PROJECT CONTEXT NAV ── */
          <>
            {/* Back to Projects */}
            <button
              onClick={() => navigate("/app")}
              className={`flex items-center gap-2 w-full mx-1.5 my-1 rounded-lg text-[11px] font-medium transition-all duration-150 ${
                isExpanded ? "px-2.5 py-[7px]" : "justify-center px-1.5 py-2"
              }`}
              style={{ color: "var(--tga-blue)" }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "var(--tga-blue-hover)"; e.currentTarget.style.backgroundColor = "var(--tga-sb-hover-bg)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "var(--tga-blue)"; e.currentTarget.style.backgroundColor = ""; }}
              data-testid="ctx-back-to-projects"
            >
              <ArrowLeft className="w-3.5 h-3.5 shrink-0" />
              <span
                className="overflow-hidden whitespace-nowrap"
                style={{ opacity: isExpanded ? 1 : 0, width: isExpanded ? "auto" : 0, transition: "opacity 0.15s ease" }}
              >
                Zurueck zu Projekte
              </span>
            </button>

            {/* Divider */}
            <div className="mx-2.5 my-1.5" style={{ borderBottom: "1px solid var(--tga-sb-border)" }} />

            {/* Project nav items */}
            {projectNavItems.map(renderNavLink)}
          </>
        ) : (
          /* ── GLOBAL NAV (Grouped) ── */
          visibleSections.map((section, si) => (
            <React.Fragment key={section.label}>
              {si > 0 && (
                <div className="mx-2.5 my-2" style={{ borderBottom: "1px solid var(--tga-sb-border)" }} />
              )}
              {isExpanded && (
                <p
                  className="px-3 pt-2 pb-0.5 text-[9px] font-bold tracking-[0.1em] uppercase select-none"
                  style={{ color: "var(--tga-sb-label)" }}
                  data-testid={`sidebar-section-${section.label.toLowerCase()}`}
                >
                  {section.label}
                </p>
              )}
              {section.items.map(renderNavLink)}
              {/* Subsections */}
              {(section.subsections || []).map(sub => {
                const subKey = `${section.label}::${sub.label}`;
                const isCollapsed = collapsedSubs.has(subKey);
                return (
                  <React.Fragment key={sub.label}>
                    {isExpanded && (
                      <button
                        type="button"
                        onClick={() => toggleSubCollapse(subKey)}
                        className="flex items-center gap-1 w-full px-3 pt-1.5 pb-0.5 text-[9px] font-semibold uppercase select-none opacity-70 hover:opacity-100 transition-opacity text-left"
                        style={{ color: "var(--tga-sb-label)", paddingLeft: 20 }}
                        data-testid={`sidebar-subsection-toggle-${sub.label.toLowerCase()}`}
                      >
                        <ChevronDown
                          className="w-2.5 h-2.5 transition-transform"
                          style={{ transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)" }}
                        />
                        <span className="flex-1">{sub.label}</span>
                        {isCollapsed && (
                          <span className="text-[9px] opacity-60">{sub.items.length}</span>
                        )}
                      </button>
                    )}
                    {!isCollapsed && sub.items.map(renderNavLink)}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          ))
        )}
      </nav>
      )}
      {/* Footer */}
      <div className="shrink-0" style={{ borderTop: "1px solid var(--tga-sb-border)" }}>
        {/* Notification Bell (admin only) */}
        {isAdmin && (
          <div className="relative">
            <button
              ref={notifBtnRef}
              onClick={handleBellClick}
              className={`flex items-center gap-2 w-full ${isExpanded ? "px-2.5 py-2" : "justify-center py-2"} transition-all duration-150`}
              style={{ color: "var(--tga-sb-text)" }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "var(--tga-sb-text-hover)"; e.currentTarget.style.backgroundColor = "var(--tga-sb-hover-bg)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "var(--tga-sb-text)"; e.currentTarget.style.backgroundColor = ""; }}
              title="Benachrichtigungen"
              data-testid="notification-bell"
            >
              <div className="relative">
                <Bell className="w-4 h-4 shrink-0" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center" style={{ boxShadow: "0 0 0 2px var(--tga-sb-bg)" }} data-testid="notification-badge">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </div>
              <span
                className="text-[12px] font-medium overflow-hidden whitespace-nowrap"
                style={{ opacity: isExpanded ? 1 : 0, width: isExpanded ? "auto" : 0, transition: "opacity 0.15s ease" }}
              >
                {t("nav.benachrichtigungen")}
              </span>
            </button>
            {notifOpen && (
              <div
                ref={notifPanelRef}
                className="fixed z-[9999] w-80 max-h-[420px] rounded-xl flex flex-col overflow-hidden"
                style={{
                  left: isExpanded ? expandedWidth + 6 : COLLAPSED_W + 6,
                  bottom: 60,
                  backgroundColor: "var(--tga-bg-card)",
                  border: "1px solid var(--tga-border)",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)",
                }}
                data-testid="notification-panel"
              >
                <div className="flex items-center justify-between px-3.5 py-2.5" style={{ borderBottom: "1px solid var(--tga-border)" }}>
                  <span className="text-[12px] font-semibold" style={{ color: "var(--tga-text-primary)" }}>Benachrichtigungen</span>
                  <div className="flex items-center gap-1.5">
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className="text-[10px] text-blue-400 hover:text-blue-300 flex items-center gap-0.5 transition-colors" data-testid="mark-all-read-btn">
                        <CheckCheck className="w-3 h-3" /> Alle gelesen
                      </button>
                    )}
                    <button onClick={() => setNotifOpen(false)} className="ml-1 transition-colors" style={{ color: "var(--tga-text-muted)" }} onMouseEnter={(e) => { e.currentTarget.style.color = "var(--tga-text-primary)"; }} onMouseLeave={(e) => { e.currentTarget.style.color = "var(--tga-text-muted)"; }} data-testid="close-notifications-btn">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="overflow-y-auto flex-1">
                  {notifications.length === 0 ? (
                    <p className="text-[11px] text-center py-8" style={{ color: "var(--tga-text-muted)" }}>Keine Benachrichtigungen</p>
                  ) : (
                    notifications.map((n) => (
                      <div
                        key={n.id}
                        className={`px-3.5 py-2.5 cursor-pointer transition-all duration-150 ${
                          !n.read ? "bg-blue-500/[0.08]" : ""
                        }`}
                        style={{ borderBottom: "1px solid var(--tga-border)" }}
                        onMouseEnter={(e) => { if (n.read) e.currentTarget.style.backgroundColor = "var(--tga-bg-hover)"; }}
                        onMouseLeave={(e) => { if (n.read) e.currentTarget.style.backgroundColor = ""; }}
                        onClick={() => { if (!n.read) markRead(n.id); }}
                        data-testid={`notification-${n.id}`}
                      >
                        <div className="flex items-start gap-2.5">
                          <MapPin className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${n.type === "gps_ausserhalb" ? "text-orange-400" : "text-blue-400"}`} />
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-medium leading-tight" style={{ color: "var(--tga-text-primary)" }}>{n.title}</p>
                            <p className="text-[10px] leading-tight mt-0.5" style={{ color: "var(--tga-text-muted)" }}>{n.message}</p>
                            <p className="text-[9px] mt-1" style={{ color: "var(--tga-text-dim)" }}>
                              {n.created_at ? new Date(n.created_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}
                            </p>
                          </div>
                          {!n.read && <span className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0 mt-1.5" />}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        <div
          className="flex items-center gap-2 px-3 py-2 overflow-hidden"
          style={{ height: isExpanded ? "auto" : 0, opacity: isExpanded ? 1 : 0, transition: "opacity 0.15s ease" }}
        >
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-medium truncate" style={{ color: "var(--tga-sb-text)" }}>{user?.name}</p>
            <p className="text-[10px] truncate" style={{ color: "var(--tga-sb-icon-dim)" }}>{user?.email}</p>
          </div>
        </div>
        {/* Admin Build-Badge — Deploy-Verifikation für Admins */}
        {isAdmin && (
          <div
            className={`px-3 ${isExpanded ? "pb-1.5" : "pb-1"}`}
            data-testid="admin-build-badge"
          >
            {isExpanded ? (
              <button
                type="button"
                onClick={() => setChangelogOpen(true)}
                title={`Klick: Was wurde geändert\n\n${BUILD_LABEL}\nVersion ${BUILD_VERSION}\nCommit ${BUILD_COMMIT}`}
                className="w-full flex items-center gap-1.5 px-2 py-1 rounded border font-mono text-[9px] leading-none cursor-pointer transition-colors"
                style={{
                  backgroundColor: "rgba(59,130,246,0.08)",
                  borderColor: "rgba(59,130,246,0.25)",
                  color: "rgb(147,197,253)",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(59,130,246,0.18)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(59,130,246,0.08)")}
                data-testid="build-label-btn"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse flex-shrink-0" />
                <span className="font-bold truncate" data-testid="build-label">{BUILD_LABEL}</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setChangelogOpen(true)}
                className="w-full flex justify-center cursor-pointer"
                title={BUILD_LABEL}
                data-testid="build-label-btn-collapsed"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
              </button>
            )}
          </div>
        )}
        <ChangelogModal open={changelogOpen} onClose={() => setChangelogOpen(false)} currentLabel={BUILD_LABEL} />

        <div className={`flex items-center ${isExpanded ? "justify-start px-3" : "justify-center"} pb-2.5`}>
          <button
            onClick={() => { logout(); navigate("/login"); }}
            className="flex items-center gap-1.5 text-[11px] hover:text-red-400 transition-colors duration-150"
            style={{ color: "var(--tga-sb-icon-dim)" }}
            data-testid="sidebar-logout"
          >
            <LogOut className="w-3.5 h-3.5 shrink-0" />
            <span style={{ opacity: isExpanded ? 1 : 0, width: isExpanded ? "auto" : 0, overflow: "hidden", transition: "opacity 0.15s ease" }}>{t("nav.abmelden")}</span>
          </button>
        </div>
      </div>

      {/* Resize handle on right edge */}
      {isExpanded && (
        <div
          className="absolute top-0 right-0 h-full z-50 group"
          onMouseDown={(e) => { e.preventDefault(); setIsResizing(true); }}
          style={{ width: 4, cursor: "ew-resize" }}
          title="Breite ziehen"
          data-testid="sidebar-resize-handle"
        >
          <div
            className="h-full transition-colors"
            style={{ backgroundColor: isResizing ? "rgb(59 130 246)" : "transparent" }}
          />
        </div>
      )}
    </aside>
  );
}

export { COLLAPSED_W, EXPANDED_W };
